import { Component, ElementRef, EventEmitter, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { ModalBoxService } from 'modal-box';
import {
  AssessmentKey,
  LaparoscopyAnalysisResult,
  ReplayAnalysisExportHeader,
  ReplayAnalysisViewerData
} from 'src/app/models/winco/laparoscopy-analysis-result.model';

@Component({
  selector: 'app-replay-viewer-analysis',
  templateUrl: './replay-viewer-analysis.component.html',
  styleUrl: './replay-viewer-analysis.component.scss',
  host: {
    '[class.export-mode]': 'renderForExport'
  }
})
export class ReplayViewerAnalysisComponent {
  private readonly assessmentToneClassMap: Record<AssessmentKey, string> = {
    excellent: 'state-positive',
    adequate: 'state-positive',
    reasonable: 'state-caution',
    tight: 'state-negative',
    insufficient: 'state-negative'
  };

  private readonly assessmentScoreMap: Record<AssessmentKey, number> = {
    excellent: 90,
    adequate: 78,
    reasonable: 68,
    tight: 52,
    insufficient: 38
  };

  @Input() analysis: LaparoscopyAnalysisResult | undefined;
  @Input() score: number | null = null;
  @Input() exportHeader: ReplayAnalysisExportHeader | null = null;
  @Input() renderForExport = false;
  @Output() rendered = new EventEmitter<void>();

  exerciseScore: number | null = null;
  safeExplanation: SafeHtml = '';

  radarChartType: ChartType = 'radar';

  decorativeRadarData: ChartData<'radar'> = {
    labels: [],
    datasets: []
  };

  decorativeRadarOptions: ChartConfiguration<'radar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        beginAtZero: true,
        min: 0,
        max: 100,
        ticks: {
          stepSize: 20,
          backdropColor: 'transparent',
          showLabelBackdrop: false,
          color: '',
          z: 2
        },
        pointLabels: {
          color: '',
          font: {
            size: 12,
            family: ''
          }
        },
        angleLines: {
          color: ''
        },
        grid: {
          color: '',
          z: 1
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: '',
        borderColor: '',
        borderWidth: 1,
        titleColor: '',
        bodyColor: '',
        callbacks: {
          label: (context) => {
            const label = context.label ?? '';
            const value = context.raw ?? '';
            return `${label}: ${value}`;
          }
        }
      }
    }
  };

  constructor(
    private modalBoxService: ModalBoxService,
    private sanitizer: DomSanitizer,
    private elementRef: ElementRef<HTMLElement>,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    if (this.analysis) {
      this.initializeView(this.analysis, this.score);
      return;
    }

    setTimeout(() => {
      const modalData = this.modalBoxService.data as ReplayAnalysisViewerData | LaparoscopyAnalysisResult;
      const payload = (modalData && 'analysis' in modalData)
        ? modalData
        : { analysis: modalData as LaparoscopyAnalysisResult, score: null };

      this.initializeView(payload.analysis, payload.score ?? null);
    }, 250);
  }

  ngOnChanges() {
    if (this.analysis) {
      this.initializeView(this.analysis, this.score);
    }
  }

  get criticalErrorsCount(): number {
    return this.analysis?.errors_detected?.filter(x => x.severity === 'major').length ?? 0;
  }

  get totalStrengths(): number {
    return this.analysis?.strengths?.length ?? 0;
  }

  get totalImprovements(): number {
    return this.analysis?.areas_for_improvement?.length ?? 0;
  }

  get executiveSummary(): string {
    return this.analysis?.summary?.overall_assessment
      ?? this.getAssessmentLabel(this.analysis?.final_result_quality?.assessment)
      ?? this.translate.instant('analisis_replay_sin_resumen');
  }

  getAssessmentToneClass(assessment?: string | null): string {
    const normalizedAssessment = this.normalizeAssessment(assessment);
    return normalizedAssessment ? this.assessmentToneClassMap[normalizedAssessment] : 'state-info';
  }

  getAssessmentLabel(assessment?: string | null): string {
    const normalizedAssessment = this.normalizeAssessment(assessment);
    return normalizedAssessment
      ? this.translate.instant(`analisis_replay_assessment_${normalizedAssessment}`)
      : this.translate.instant('analisis_replay_sin_datos_corto');
  }

  getSeverityLabel(severity?: string | null): string {
    return severity
      ? this.translate.instant(`analisis_replay_severity_${severity.toLowerCase()}`)
      : this.translate.instant('analisis_replay_sin_datos_corto');
  }

  getPriorityLabel(priority?: string | null): string {
    return priority
      ? this.translate.instant(`analisis_replay_priority_${priority.toLowerCase()}`)
      : this.translate.instant('analisis_replay_sin_datos_corto');
  }

  private initializeView(analysis: LaparoscopyAnalysisResult, score: number | null): void {
    this.analysis = analysis;
    this.exerciseScore = score ?? null;
    this.safeExplanation = this.sanitizer.bypassSecurityTrustHtml(this.analysis?.explanation ?? '');
    this.configureRadarTheme();
    this.buildDecorativeRadar();
    this.scheduleRenderedEmission();
  }

  private scheduleRenderedEmission(): void {
    const emitDelay = this.renderForExport ? 350 : 0;
    setTimeout(() => this.rendered.emit(), emitDelay);
  }

  private configureRadarTheme(): void {
    const textColor = this.getCssVar('--analysis-radar-text');
    const lineColor = this.getCssVar('--analysis-radar-line');
    const gridColor = this.getCssVar('--analysis-radar-grid');
    const tooltipBackground = this.getCssVar('--analysis-radar-tooltip-background');
    const tooltipBorder = this.getCssVar('--analysis-radar-tooltip-border');

    this.decorativeRadarOptions = {
      ...this.decorativeRadarOptions,
      scales: {
        r: {
          ...this.decorativeRadarOptions?.scales?.r,
          ticks: {
            ...this.decorativeRadarOptions?.scales?.r?.ticks,
            color: textColor,
            backdropColor: 'transparent',
            showLabelBackdrop: false,
            z: 2
          },
          pointLabels: {
            color: textColor,
            font: {
              size: 12,
              family: this.getCssVar('--app-font-family-base')
            }
          },
          angleLines: {
            color: gridColor
          },
          grid: {
            color: gridColor,
            z: 1
          }
        }
      },
      plugins: {
        ...this.decorativeRadarOptions?.plugins,
        tooltip: {
          ...this.decorativeRadarOptions?.plugins?.tooltip,
          backgroundColor: tooltipBackground,
          borderColor: tooltipBorder,
          titleColor: textColor,
          bodyColor: textColor,
          borderWidth: 1,
          callbacks: this.decorativeRadarOptions?.plugins?.tooltip?.callbacks
        }
      }
    };
  }

  private buildDecorativeRadar(): void {
    if (!this.analysis) return;

    const labels = [
      this.translate.instant('analisis_replay_control_instrumental'),
      this.translate.instant('analisis_replay_precision'),
      this.translate.instant('analisis_replay_eficiencia_corta'),
      this.translate.instant('analisis_replay_coordinacion_bimanual'),
      this.translate.instant('analisis_replay_seguridad_corta'),
      this.translate.instant('analisis_replay_campo_visual_corto')
    ];

    const values = [
      this.getDecorativeScoreFromAssessment(this.analysis.technical_performance?.instrument_handling?.assessment),
      this.getDecorativeScoreFromAssessment(this.analysis.technical_performance?.precision?.assessment),
      this.getDecorativeScoreFromAssessment(this.analysis.technical_performance?.movement_efficiency?.assessment),
      this.getDecorativeScoreFromAssessment(this.analysis.technical_performance?.bimanual_coordination?.assessment),
      this.getDecorativeScoreFromAssessment(this.analysis.technical_performance?.task_safety?.assessment),
      this.getDecorativeScoreFromAssessment(this.analysis.technical_performance?.visual_field_management?.assessment)
    ];

    this.decorativeRadarData = {
      labels,
      datasets: [
        {
          data: values,
          label: this.translate.instant('analisis_replay_perfil_visual'),
          borderColor: this.getCssVar('--analysis-radar-line'),
          backgroundColor: this.getCssVar('--analysis-radar-fill'),
          borderWidth: 2,
          pointBackgroundColor: this.getCssVar('--analysis-radar-line'),
          pointBorderColor: this.getCssVar('--analysis-radar-line'),
          pointRadius: 3,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: this.getCssVar('--analysis-radar-line'),
          pointHoverBorderColor: this.getCssVar('--analysis-radar-line')
        }
      ]
    };
  }

  private getCssVar(name: string): string {
    if (typeof window === 'undefined') {
      return '';
    }

    return getComputedStyle(this.elementRef.nativeElement).getPropertyValue(name).trim();
  }

  /**
   * Valor puramente decorativo para alimentar el radar.
   * No representa una puntuacion real del analisis.
   */
  private getDecorativeScoreFromAssessment(assessment?: string | null): number {
    const normalizedAssessment = this.normalizeAssessment(assessment);
    return normalizedAssessment ? this.assessmentScoreMap[normalizedAssessment] : 50;
  }

  private normalizeAssessment(assessment?: string | null): AssessmentKey | null {
    const value = (assessment ?? '').trim().toLowerCase();

    if (
      value === 'excellent'
      || value === 'adequate'
      || value === 'reasonable'
      || value === 'tight'
      || value === 'insufficient'
    ) {
      return value;
    }

    return null;
  }
}
